/*
 * Copyright 2026 Sony Corporation
 */

import { GizmoManager } from '@babylonjs/core/Gizmos/gizmoManager';
import { PointerEventTypes } from '@babylonjs/core/Events/pointerEvents';
import type { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import type { IAxisDragGizmo } from '@babylonjs/core/Gizmos/axisDragGizmo';
import type { TransformNode } from '@babylonjs/core/Meshes/transformNode';

import { createThrottle } from './throttle';
import type { SceneEditor } from './scene-editor';
import type { Transform, Vec3 } from '../scene-doc/schema';

export type GizmoMode = 'select' | 'move' | 'rotate' | 'scale';

export interface NodeGizmo {
  attach: (node: TransformNode | undefined) => void;
  setMode: (mode: GizmoMode) => void;
  /** After the editor is reframed, so handles stay a usable size at any scale. */
  refit: () => void;
  dispose: () => void;
}

export interface NodeGizmoOptions {
  editor: SceneEditor;
  /** Called at most once per interval while dragging, and once more on release. */
  onTransformChanged: (transform: Transform) => void;
  /** A click in the viewport; undefined when it missed everything. */
  onPicked: (mesh: AbstractMesh | undefined) => void;
  throttleMs?: number;
}

/** 10 Hz while dragging, matching the camera rigs (devplan §3.9). */
const DEFAULT_THROTTLE_MS = 100;

/** One document node's worth of drag, keyed for the throttle. */
const DRAG_KEY = 'node';

/**
 * How far a scale drag goes, against Babylon's default of 1.
 *
 * At the default a drag the width of the object barely resizes it, so blocking
 * a scene out means several drags where one should do. Position and rotation
 * are already one-to-one with the pointer and need no such factor.
 */
const SCALE_SENSITIVITY = 3;

const toVec3 = (vector: { x: number; y: number; z: number }): Vec3 => [vector.x, vector.y, vector.z];

/** A sub-gizmo's draggable parts. */
export interface GizmoHandles {
  xGizmo: IAxisDragGizmo;
  yGizmo: IAxisDragGizmo;
  zGizmo: IAxisDragGizmo;
  /** The scale gizmo's centre box, which is not filed under an axis. */
  uniformScaleGizmo?: IAxisDragGizmo;
}

/**
 * Every handle, not just the three axes.
 *
 * A handle nothing listens to still moves the mesh, so the drag looks like it
 * worked — until the next sync puts the document's untouched transform back and
 * the work is silently undone.
 */
export const handlesOf = (gizmo: GizmoHandles): IAxisDragGizmo[] =>
  [gizmo.xGizmo, gizmo.yGizmo, gizmo.zGizmo, gizmo.uniformScaleGizmo].filter((handle) => handle !== undefined);

/**
 * The transform gizmo for document nodes.
 *
 * It is the Scene page's counterpart to `rig-manager`, and deliberately a
 * different object: that one moves cameras, which are twin data, and this one
 * moves scenery. A screen holds one or the other, never both (devplan §4).
 *
 * A drag never writes to the document directly — it emits `set_transform`, and
 * the mesh moves again when the reducer's result is applied. One writer.
 */
export const createNodeGizmo = ({
  editor,
  onTransformChanged,
  onPicked,
  throttleMs = DEFAULT_THROTTLE_MS,
}: NodeGizmoOptions): NodeGizmo => {
  const gizmos = new GizmoManager(editor.scene);
  gizmos.usePointerToAttachGizmos = false;

  let attached: TransformNode | undefined;
  let mode: GizmoMode = 'select';

  const throttle = createThrottle<Transform>({
    intervalMs: throttleMs,
    send: (_key, transform) => onTransformChanged(transform),
  });

  const read = (): Transform | undefined =>
    attached
      ? {
          position: toVec3(attached.position),
          rotation: toVec3(attached.rotation),
          scale: toVec3(attached.scaling),
        }
      : undefined;

  const publish = (final: boolean) => {
    const transform = read();
    if (!transform) {
      return;
    }
    throttle.push(DRAG_KEY, transform);
    if (final) {
      throttle.flush(DRAG_KEY);
    }
  };

  // A sub-gizmo is built the first time its mode is enabled, so binding happens
  // after each toggle rather than once up front.
  const bound = new Set<object>();
  const bind = (gizmo: GizmoHandles | null): void => {
    if (!gizmo || bound.has(gizmo)) {
      return;
    }
    bound.add(gizmo);
    for (const handle of handlesOf(gizmo)) {
      // Drag fires every frame; release is the one update that must not be dropped.
      handle.dragBehavior.onDragObservable.add(() => publish(false));
      handle.dragBehavior.onDragEndObservable.add(() => publish(true));
    }
  };

  const applyMode = () => {
    gizmos.positionGizmoEnabled = mode === 'move';
    gizmos.rotationGizmoEnabled = mode === 'rotate';
    gizmos.scaleGizmoEnabled = mode === 'scale';

    bind(gizmos.gizmos.positionGizmo);
    bind(gizmos.gizmos.rotationGizmo);
    bind(gizmos.gizmos.scaleGizmo);

    if (gizmos.gizmos.scaleGizmo) {
      gizmos.gizmos.scaleGizmo.sensitivity = SCALE_SENSITIVITY;
    }

    gizmos.attachToNode(mode === 'select' ? null : (attached ?? null));
  };

  const pointer = editor.scene.onPointerObservable.add((info) => {
    if (info.type !== PointerEventTypes.POINTERPICK) {
      return;
    }
    onPicked(info.pickInfo?.hit ? (info.pickInfo.pickedMesh ?? undefined) : undefined);
  });

  return {
    attach: (node) => {
      attached = node;
      applyMode();
    },

    setMode: (next) => {
      mode = next;
      applyMode();
    },

    refit: () => {
      const ratio = Math.max(editor.activeBounds().radius / 10, 0.5);
      for (const gizmo of [gizmos.gizmos.positionGizmo, gizmos.gizmos.rotationGizmo, gizmos.gizmos.scaleGizmo]) {
        if (gizmo) {
          gizmo.scaleRatio = ratio;
        }
      }
    },

    dispose: () => {
      throttle.cancel();
      editor.scene.onPointerObservable.remove(pointer);
      gizmos.dispose();
    },
  };
};
